/**
 * SearchResultsPage Component
 * Full search results page with grouped results by type
 * Features:
 * - Search results grouped by content type
 * - Type tabs for filtering
 * - Result count and pagination
 * - Empty states
 * - Loading states
 */

import { useMemo, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Search,
  FileText,
  Hash,
  Image,
  Table,
  Mail,
  Presentation,
  ChevronLeft,
  ChevronRight,
  Inbox,
  ArrowLeft,
} from 'lucide-react'
import { SearchBar } from './SearchBar'
import { SearchFilters } from './SearchFilters'
import { SearchResultCard } from './SearchResultCard'
import { useContentSearch, useContentTags, useContentFolderTree } from '@/lib/hooks/use-content-hub'
import type { ContentItem, ContentItemType, ContentSearchParams } from '@/lib/api/types'

// ============================================
// Types
// ============================================

export interface SearchResultsPageProps {
  className?: string
}

// ============================================
// Constants
// ============================================

const CONTENT_TYPE_TABS: Array<{
  value: ContentItemType | 'all'
  label: string
  icon: React.ElementType
}> = [
  { value: 'all', label: 'All', icon: Search },
  { value: 'Text', label: 'Text', icon: FileText },
  { value: 'DataPoint', label: 'Data', icon: Hash },
  { value: 'Image', label: 'Images', icon: Image },
  { value: 'Table', label: 'Tables', icon: Table },
  { value: 'EmailSnippet', label: 'Emails', icon: Mail },
  { value: 'SlideElement', label: 'Slides', icon: Presentation },
]

const PAGE_SIZE = 20

// ============================================
// SearchResultsPage Component
// ============================================

export function SearchResultsPage({ className }: SearchResultsPageProps) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Get search params from URL
  const query = searchParams.get('q') || ''
  const typeFilter = (searchParams.get('type') as ContentItemType | 'all') || 'all'
  const folderId = searchParams.get('folder') || undefined
  const tagsParam = searchParams.get('tags')
  const selectedTags = tagsParam ? tagsParam.split(',') : []
  const sortBy = searchParams.get('sortBy') || 'relevance'
  const sortOrder = (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc'
  const page = parseInt(searchParams.get('page') || '1', 10)

  // Fetch tags and folders for filters
  const { data: tags = [] } = useContentTags()
  const { data: folderTree = [] } = useContentFolderTree()

  // Flatten folder tree for filter
  const flatFolders = useMemo(() => {
    const result: Array<{ id: string; name: string; path: string }> = []
    const flatten = (folders: typeof folderTree, path = '') => {
      for (const folder of folders) {
        const currentPath = path ? `${path} / ${folder.name}` : folder.name
        result.push({ id: folder.id, name: folder.name, path: currentPath })
        if (folder.children) {
          flatten(folder.children, currentPath)
        }
      }
    }
    flatten(folderTree)
    return result
  }, [folderTree])

  // Build search params
  const searchApiParams: ContentSearchParams = useMemo(
    () => ({
      query,
      types: typeFilter !== 'all' ? [typeFilter] : undefined,
      folderId,
      tags: selectedTags.length > 0 ? selectedTags : undefined,
      page,
      pageSize: PAGE_SIZE,
      sortBy: sortBy as ContentSearchParams['sortBy'],
      sortOrder,
    }),
    [query, typeFilter, folderId, selectedTags, page, sortBy, sortOrder]
  )

  // Fetch search results
  const { data: searchResult, isLoading, error } = useContentSearch(searchApiParams)

  // Update URL params helper
  const updateParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      const newParams = new URLSearchParams(searchParams)
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value)
        } else {
          newParams.delete(key)
        }
      })
      // Reset page when filters change (except when changing page itself)
      if (!('page' in updates)) {
        newParams.delete('page')
      }
      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  // Handlers
  const handleSearch = useCallback(
    (newQuery: string) => {
      updateParams({ q: newQuery || undefined })
    },
    [updateParams]
  )

  const handleTypeChange = useCallback(
    (type: string) => {
      updateParams({ type: type === 'all' ? undefined : type })
    },
    [updateParams]
  )

  const handleTypesChange = useCallback(
    (types: ContentItemType[]) => {
      // For multi-select from filters, use first type or clear
      updateParams({ type: types.length > 0 ? types[0] : undefined })
    },
    [updateParams]
  )

  const handleFolderChange = useCallback(
    (folder: string | undefined) => {
      updateParams({ folder })
    },
    [updateParams]
  )

  const handleTagsChange = useCallback(
    (newTags: string[]) => {
      updateParams({ tags: newTags.length > 0 ? newTags.join(',') : undefined })
    },
    [updateParams]
  )

  const handleSortChange = useCallback(
    (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
      updateParams({
        sortBy: newSortBy === 'relevance' ? undefined : newSortBy,
        sortOrder: newSortOrder === 'desc' ? undefined : newSortOrder,
      })
    },
    [updateParams]
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateParams({ page: newPage > 1 ? String(newPage) : undefined })
    },
    [updateParams]
  )

  const handleViewItem = useCallback(
    (item: ContentItem) => {
      navigate(`/content-hub/item/${item.id}`)
    },
    [navigate]
  )

  const handleEditItem = useCallback(
    (item: ContentItem) => {
      navigate(`/content-hub/item/${item.id}/edit`)
    },
    [navigate]
  )

  // Calculate type counts from results
  const typeCounts = useMemo(() => {
    if (!searchResult?.facets?.types) return {}
    return searchResult.facets.types.reduce(
      (acc, t) => {
        acc[t.type] = t.count
        return acc
      },
      {} as Record<string, number>
    )
  }, [searchResult?.facets?.types])

  // Total count
  const totalCount = searchResult?.totalCount ?? 0
  const totalPages = searchResult?.totalPages ?? 0
  const items = searchResult?.items ?? []

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => navigate('/content-hub')}
          className="h-9 w-9 rounded-lg"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            Search Results
          </h1>
          {query && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Showing results for "{query}"
            </p>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <SearchBar
        onSearch={handleSearch}
        placeholder="Search content..."
        className="max-w-2xl"
        showFullSearch={false}
      />

      {/* Filters */}
      <SearchFilters
        selectedTypes={typeFilter !== 'all' ? [typeFilter] : []}
        onTypesChange={handleTypesChange}
        selectedFolderId={folderId}
        onFolderChange={handleFolderChange}
        selectedTags={selectedTags}
        onTagsChange={handleTagsChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        folders={flatFolders}
        tags={tags}
      />

      {/* Type Tabs */}
      <Tabs value={typeFilter} onValueChange={handleTypeChange}>
        <TabsList className="h-10 p-1 bg-neutral-100 dark:bg-neutral-800 rounded-xl">
          {CONTENT_TYPE_TABS.map((tab) => {
            const Icon = tab.icon
            const count = tab.value === 'all' ? totalCount : typeCounts[tab.value] || 0

            return (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className={cn(
                  'gap-1.5 rounded-lg',
                  'data-[state=active]:bg-white dark:data-[state=active]:bg-neutral-700'
                )}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
                {count > 0 && (
                  <span className="ml-1 text-xs text-neutral-500 dark:text-neutral-400">
                    {count}
                  </span>
                )}
              </TabsTrigger>
            )
          })}
        </TabsList>
      </Tabs>

      {/* Results */}
      <div className="space-y-4">
        {/* Loading State */}
        {isLoading && (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="p-4 rounded-xl border border-neutral-200 dark:border-neutral-700"
              >
                <div className="flex items-start gap-4">
                  <Skeleton className="h-12 w-12 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/4" />
                    <Skeleton className="h-5 w-2/3" />
                    <Skeleton className="h-4 w-full" />
                    <div className="flex gap-2">
                      <Skeleton className="h-5 w-16" />
                      <Skeleton className="h-5 w-16" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-8 text-center">
            <p className="text-red-500 dark:text-red-400">
              Error loading search results. Please try again.
            </p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && items.length === 0 && (
          <div className="p-12 text-center">
            <div className="inline-flex p-4 rounded-full bg-neutral-100 dark:bg-neutral-800 mb-4">
              <Inbox className="h-8 w-8 text-neutral-400" />
            </div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
              No results found
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4 max-w-md mx-auto">
              {query
                ? `No content matches "${query}". Try adjusting your search or filters.`
                : 'Try searching for content or adjusting your filters.'}
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/content-hub')}
            >
              Browse Content Hub
            </Button>
          </div>
        )}

        {/* Results List */}
        {!isLoading && !error && items.length > 0 && (
          <>
            {/* Result count */}
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {totalCount} result{totalCount !== 1 ? 's' : ''} found
              {page > 1 && ` (page ${page} of ${totalPages})`}
            </p>

            {/* Results */}
            <div className="space-y-3">
              {items.map((item) => (
                <SearchResultCard
                  key={item.id}
                  item={item}
                  searchQuery={query}
                  onView={handleViewItem}
                  onEdit={handleEditItem}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                  className="gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number
                    if (totalPages <= 5) {
                      pageNum = i + 1
                    } else if (page <= 3) {
                      pageNum = i + 1
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i
                    } else {
                      pageNum = page - 2 + i
                    }

                    return (
                      <Button
                        key={pageNum}
                        type="button"
                        variant={page === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="h-8 w-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    )
                  })}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="gap-1"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default SearchResultsPage
